import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function BostonToIreland() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openRelocationExpert = () => {
    const expertButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (expertButton) {
      expertButton.click();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      <article className="pt-24 pb-16">
        {/* Hero Section */}
        <div className="relative min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/f6b1bac3bc8ef387690619ae4ac7cb4a.jpg"
              alt="Boston to Dublin Ireland relocation comparison"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
              <span className="text-2xl">🇺🇸</span>
              <i className="ri-arrow-right-line text-white text-xl"></i>
              <span className="text-2xl">🇮🇪</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Moving to Ireland from Boston: Complete Guide for Bostonians
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Everything Boston residents need to know about relocating to Ireland in 2024
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Boston and Ireland share deep historical and cultural connections, making the transition from Boston to Ireland particularly meaningful for many Massachusetts residents. Whether you're drawn by ancestral roots, career opportunities in Dublin's thriving tech sector, or the appeal of European living, this comprehensive guide covers everything Bostonians need to know about relocating to Ireland.
            </p>
          </div>

          {/* Cost Comparison */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Boston vs Dublin: Cost of Living Comparison</h2>
            
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Category</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Boston, MA</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Dublin, Ireland</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Difference</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">1-Bed Apartment (City Center)</td>
                      <td className="py-3 px-4">$3,200/month</td>
                      <td className="py-3 px-4">$2,100/month</td>
                      <td className="py-3 px-4 text-green-600 font-semibold">Save $1,100/mo</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Groceries (Monthly)</td>
                      <td className="py-3 px-4">$450</td>
                      <td className="py-3 px-4">$400</td>
                      <td className="py-3 px-4 text-green-600 font-semibold">Save $50/mo</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Public Transit Pass</td>
                      <td className="py-3 px-4">$90 (MBTA)</td>
                      <td className="py-3 px-4">$140 (Leap Card)</td>
                      <td className="py-3 px-4 text-red-600 font-semibold">+$50/mo</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Healthcare (Annual)</td>
                      <td className="py-3 px-4">$7,000+</td>
                      <td className="py-3 px-4">$1,500</td>
                      <td className="py-3 px-4 text-green-600 font-semibold">Save $5,500/yr</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Dining Out (Meal)</td>
                      <td className="py-3 px-4">$25</td>
                      <td className="py-3 px-4">$20</td>
                      <td className="py-3 px-4 text-green-600 font-semibold">Save $5</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-semibold">Pint of Beer</td>
                      <td className="py-3 px-4">$8 (Sam Adams)</td>
                      <td className="py-3 px-4">$7 (Guinness)</td>
                      <td className="py-3 px-4 text-green-600 font-semibold">Save $1</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-emerald-900 mb-3">Bottom Line for Bostonians</h3>
              <p className="text-emerald-800">
                Most Bostonians save <strong>$1,500-2,500 per month</strong> on living expenses in Dublin, primarily due to lower housing costs and dramatically reduced healthcare expenses. The biggest savings come from not needing expensive US health insurance.
              </p>
            </div>
          </section>

          {/* Jobs Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Jobs in Ireland for Boston Professionals</h2>
            
            <p className="text-lg text-gray-700 mb-6">
              Boston's strong tech, biotech, and financial services sectors align perfectly with Ireland's top industries. Many Boston-based companies have significant operations in Ireland, making internal transfers possible for some Bostonians.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Boston Companies in Ireland</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-building-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span><strong>HubSpot:</strong> Large Dublin office (internal transfers available)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-building-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span><strong>State Street:</strong> Major operations in Dublin</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-building-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Boston Scientific:</strong> Manufacturing and R&D in Ireland</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-building-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Liberty Mutual:</strong> European headquarters in Dublin</span>
                  </li>
                </ul>
              </div>

              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">In-Demand Skills from Boston</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-code-box-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Software Engineering:</strong> €60k-120k ($65k-130k)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-medicine-bottle-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Biotech/Pharma:</strong> €55k-100k ($60k-109k)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-bank-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Financial Services:</strong> €50k-110k ($55k-120k)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-customer-service-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Customer Success:</strong> €40k-75k ($44k-82k)</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Cultural Similarities */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Bostonians Love Ireland</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-history-line',
                  title: 'Shared History',
                  description: 'Boston\'s strong Irish heritage means many Bostonians have ancestral connections to Ireland. The cultural familiarity makes the transition easier.',
                },
                {
                  icon: 'ri-football-line',
                  title: 'Sports Culture',
                  description: 'Like Boston, Ireland is passionate about sports. Trade Patriots for rugby, Red Sox for hurling, and Celtics for Gaelic football.',
                },
                {
                  icon: 'ri-beer-line',
                  title: 'Pub Culture',
                  description: 'Boston\'s neighborhood pub culture translates perfectly to Ireland. Friendly locals, great conversation, and community gathering spots.',
                },
                {
                  icon: 'ri-book-line',
                  title: 'Education Focus',
                  description: 'Both Boston and Ireland value education highly. Ireland has excellent universities and a well-educated workforce.',
                },
                {
                  icon: 'ri-music-line',
                  title: 'Live Music Scene',
                  description: 'From traditional Irish music to modern rock, Dublin\'s music scene rivals Boston\'s vibrant live music culture.',
                },
                {
                  icon: 'ri-walk-line',
                  title: 'Walkable Cities',
                  description: 'Like Boston, Irish cities are compact and walkable. Dublin\'s neighborhoods have the same intimate feel as Boston\'s.',
                },
              ].map((item, index) => (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-2xl text-emerald-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-[#0A381F] to-emerald-800 text-white rounded-2xl p-8">
              <h2 className="text-3xl font-bold mb-6">Ready to Make the Move from Boston to Ireland?</h2>
              <p className="text-lg mb-6 text-white/90">
                Our Ireland Relocation Expert can help you navigate every step of your move from Boston to Ireland. Get personalized guidance on visas, housing, jobs, and settling into Irish life.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openRelocationExpert}
                  className="bg-white text-[#0A381F] px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-all hover-lift flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-chat-voice-fill text-xl"></i>
                  Talk to Relocation Expert
                </button>
                <a
                  href="/tools/cost-calculator"
                  className="bg-[#D4AF37] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#C19B2F] transition-all hover-lift flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-calculator-line text-xl"></i>
                  Calculate Your Costs
                </a>
              </div>
            </div>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}